import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { isMobile } from 'react-device-detect';
import logo from './assets/scomodo-logo-red (1).svg';
import { MessageForm } from './components/MessageForm';
import './App.css'
import { CustomMarkerOverlay } from './components/CustomMarkerOverlay';
import { Snackbar } from './components/Snackbar';

const containerStyle = {
  width: '100vw',
  height: '100vh'
};

const center = {
  lat: 42.5,
  lng: 12.5
};

const mapStyles = [
  {
    featureType: "all",
    stylers: [
      { saturation: -100 },
      { gamma: 0.5 }
    ]
  },
  {
    featureType: "all",
    elementType: "labels",
    stylers: [{ visibility: "on" }]
  },
  {
    featureType: "poi",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "transit",
    stylers: [{ visibility: "on" }]
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ visibility: "on" }]
  },
  {
    featureType: "road",
    elementType: "labels.text.stroke",
    stylers: [{ visibility: "on" }]
  }
];



function App() {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null); // 👈 aggiunto
  const [markers, setMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [newMarkerPosition, setNewMarkerPosition] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddSnackbar, setShowAddSnackbar] = useState(false);
  const isModalOpen = selectedMarker !== null || newMarkerPosition !== null;

  const options = {
    styles: mapStyles,
    disableDefaultUI: true,
    clickableIcons: false,
    gestureHandling: (isModalOpen && isMobile) ? 'none' : 'greedy',
    maxZoom: 18
  };

  useEffect(() => {
    fetchMarkers();
  }, []);

  const fetchMarkers = async () => {
    setLoading(true); // 👈 mostra il loader

    try {
      const response = await fetch('https://qdrfojrgdd.execute-api.eu-south-1.amazonaws.com/prod/marker');
      const data = await response.json();
      setMarkers(data);
    } catch (error) {
      console.error('Failed to fetch markers:', error);
    } finally {
      setLoading(false); // 👈 nascondi loader anche in caso di errore
    }
  };

  const fetchMarkerDetails = async (id) => {
    try {
      const response = await fetch(`https://qdrfojrgdd.execute-api.eu-south-1.amazonaws.com/prod/marker?id=${encodeURIComponent(id)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch marker details:', error);
      return null;
    }
  };
  const handleMapClick = (event) => {
    if (isModalOpen) {
      return;
    }

    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };

    setSelectedMarker(null);

    if (mapRef.current) {

      // Calcola l'offset solo per dispositivi mobili
      const bounds = mapRef.current.getBounds();
      const neLat = bounds.getNorthEast().lat();
      const swLat = bounds.getSouthWest().lat();
      const latHeight = neLat - swLat;
      const offsetLat = latHeight * 0.3; // Sposta il centro della mappa più in alto

      const mobileCenteredPosition = {
        lat: newPosition.lat + offsetLat,
        lng: newPosition.lng
      };

      mapRef.current.panTo(mobileCenteredPosition);

      setTimeout(() => {
        setNewMarkerPosition(newPosition);
      }, 500);
    } else {
      setNewMarkerPosition(newPosition);
    }
  };

  const handleMarkerClick = async (marker, event) => {
    event.stop();

    setLoading(true);
  
    const markerDetails = await fetchMarkerDetails(marker.id);

    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      const neLat = bounds.getNorthEast().lat(); // Latitudine del punto nord-est
      const swLat = bounds.getSouthWest().lat(); // Latitudine del punto sud-ovest
      const latHeight = neLat - swLat; // Differenza di latitudine visibile

      let centeredPosition = {
        lat: marker.latitude,
        lng: marker.longitude
      };

      const offsetLat = latHeight * 0.3;
      centeredPosition.lat += offsetLat;

      mapRef.current.panTo(centeredPosition);

      setTimeout(() => {
        setSelectedMarker(markerDetails);
        setNewMarkerPosition(null);
        setLoading(false);
      }, 500);
    } else {
      setSelectedMarker(marker);
      setNewMarkerPosition(null);
    }
  };


  const handleCloseClick = (event) => {
    // Ferma la propagazione per prevenire eventi indesiderati sulla mappa
    if (event) {
      event.stopPropagation();
      event.preventDefault();  // Aggiunto per evitare qualsiasi azione di default che potrebbe essere associata
    }

    // Reset dei marker selezionati
    setSelectedMarker(null);
    setNewMarkerPosition(null);
  };

  const handleAddClick = () => {
    setShowAddSnackbar(true)
  };


  return (
    <div
      className="App"
      ref={mapContainerRef}
      style={{ width: '100vw', height: '100vh', position: 'relative' }}
    >

      <LoadScript googleMapsApiKey="AIzaSyA1PzCtqYpXQ4Miligux_-YZJUj-DVSPnI">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={5.6}
          options={options}
          onClick={handleMapClick}
          onLoad={map => mapRef.current = map}
        >
          {markers.map((marker) => (
            <Marker
              key={marker.id}
              position={{ lat: marker.latitude, lng: marker.longitude }}
              icon={{
                url: "/logo192.png",
                scaledSize: new window.google.maps.Size(50, 50), // Regola la dimensione
                anchor: new window.google.maps.Point(25, 50) // Centra correttamente il marker
              }}
              onClick={(event) => handleMarkerClick(marker, event)}
            />
          ))}

          {selectedMarker && (
            <CustomMarkerOverlay
              map={mapRef.current}
              position={{ lat: selectedMarker.latitude, lng: selectedMarker.longitude }}
              onClose={handleCloseClick}
              {...selectedMarker}
            />
          )}

          {newMarkerPosition && (
            <CustomMarkerOverlay
              map={mapRef.current}
              position={newMarkerPosition}
              onClose={handleCloseClick}
            >
              <MessageForm
                lat={newMarkerPosition.lat}
                lng={newMarkerPosition.lng}
                onClose={handleCloseClick}
                onSuccess={() => {
                  handleCloseClick();
                  setShowSnackbar(true);
                }}
              />
            </CustomMarkerOverlay>
          )}
        </GoogleMap>
      </LoadScript>
      <img src={logo} alt="Logo" style={{
        position: 'absolute',
        top: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '220px',
        zIndex: 1000,
        display: (isMobile && newMarkerPosition) ? 'none' : 'block'
      }} />
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '20px 30px',
            borderRadius: '10px',
            fontWeight: 'bold',
            fontFamily: 'sans-serif',
            fontSize: '16px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
          }}
        >
          Caricamento...
        </div>
      )}
      {showSnackbar && (
        <Snackbar
          message="Ci sono spazi che cambiano la vita! Grazie per aver condiviso il tuo"
          onClose={() => setShowSnackbar(false)}
        />
      )}
      {showAddSnackbar && (
        <Snackbar
          message="Tocca la mappa e segna un posto che ti ha cambiato la vita, appare subito!"
          onClose={() => setShowAddSnackbar(false)}
        />
      )}
      <button
        onClick={handleAddClick}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          backgroundColor: '#ffffff',
          color: '#000000',
          fontSize: '32px',
          fontWeight: 'bold',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
        }}
      >
        +
      </button>
    </div>
  );
}

export default App;